import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  searchTransferItems,
  transferCreateQueryKeys,
  type TransferItemSearchParams,
  type TransferItemSearchResult,
} from '@/data/transferCreate';
import './NewTransferDialog.css';

const SEARCH_DELAY_MS = 250;
const MIN_QUERY_LENGTH = 2;

/** The text after the user stops typing for `delay` ms (trimmed). */
export function useDebouncedText(value: string, delay = SEARCH_DELAY_MS): string {
  const [debounced, setDebounced] = React.useState(value.trim());
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value.trim()), delay);
    return () => window.clearTimeout(handle);
  }, [delay, value]);
  return debounced;
}

function SearchIcon() {
  return (
    <svg className="transfer-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export type SearchComboboxProps<T> = {
  /** Accessible name of the text field. */
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly T[];
  optionKey: (option: T) => string;
  renderOption: (option: T, index: number) => React.ReactNode;
  isOptionDisabled?: (option: T) => boolean;
  onSelect: (option: T) => void;
  /** Accessible name of the list. */
  listLabel: string;
  /** Whether the list may show at all (for example, enough characters typed). */
  listEnabled: boolean;
  /** A line under the options: searching, no matches, an error. */
  status?: React.ReactNode;
  /** Read out after results change ("5 matches"). */
  announcement?: string;
  busy?: boolean;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  className?: string;
};

/**
 * A text field with a list of matches (the WAI-ARIA combobox pattern): focus stays in the field,
 * Up and Down move the highlighted match (aria-activedescendant), Enter picks it, Escape closes the
 * list without closing the dialog around it, and a click picks a match without moving focus.
 * Data-agnostic: the caller searches and renders the options.
 */
export function SearchCombobox<T>({
  label,
  placeholder,
  value,
  onValueChange,
  options,
  optionKey,
  renderOption,
  isOptionDisabled,
  onSelect,
  listLabel,
  listEnabled,
  status,
  announcement,
  busy = false,
  disabled = false,
  inputRef,
  className,
}: SearchComboboxProps<T>) {
  const listId = React.useId();
  const popupRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const visible = open && listEnabled && !disabled && (options.length > 0 || Boolean(status));
  const hasOptions = options.length > 0;

  // In a short scrolling body (a phone sheet) the list can open below the fold: bring it into view.
  React.useEffect(() => {
    if (visible) popupRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [hasOptions, visible]);

  const enabledAt = React.useCallback(
    (index: number) => index >= 0 && index < options.length && !(isOptionDisabled?.(options[index]) ?? false),
    [isOptionDisabled, options],
  );

  // A new result set highlights its first pickable match.
  React.useEffect(() => {
    const first = options.findIndex((option) => !(isOptionDisabled?.(option) ?? false));
    setActiveIndex(first);
  }, [isOptionDisabled, options]);

  const optionId = React.useCallback((index: number) => `${listId}-option-${index}`, [listId]);

  React.useEffect(() => {
    if (!visible || activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, optionId, visible]);

  function move(step: 1 | -1) {
    if (options.length === 0) return;
    // With nothing highlighted, Down starts at the first match and Up at the last.
    let index = activeIndex >= 0 ? activeIndex : step === 1 ? -1 : 0;
    for (let tries = 0; tries < options.length; tries += 1) {
      index = (index + step + options.length) % options.length;
      if (enabledAt(index)) {
        setActiveIndex(index);
        return;
      }
    }
  }

  function pick(index: number) {
    if (!enabledAt(index)) return;
    onSelect(options[index]);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    onValueChange(event.target.value);
    setOpen(true);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!visible) {
          setOpen(true);
          return;
        }
        move(event.key === 'ArrowDown' ? 1 : -1);
        return;
      case 'Enter':
        // Never submit or trigger anything around the field.
        event.preventDefault();
        if (visible) pick(activeIndex);
        return;
      case 'Escape':
        if (visible) {
          // The list owns this Escape, so the dialog or page around it stays open.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
    }
  }

  function keepFocus(event: React.MouseEvent) {
    event.preventDefault();
  }

  return (
    <div className={['transfer-search', className].filter(Boolean).join(' ')} data-open={visible ? 'true' : undefined}>
      <div className="transfer-search__field" data-disabled={disabled ? 'true' : undefined}>
        <SearchIcon />
        <input
          ref={inputRef}
          type="text"
          className="transfer-search__input"
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={visible}
          aria-controls={visible && options.length > 0 ? listId : undefined}
          aria-activedescendant={visible && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-busy={busy || undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        />
        {busy ? <span className="transfer-search__spinner" aria-hidden="true" /> : null}
      </div>
      {visible ? (
        // A press anywhere in the list (an option, its scrollbar) keeps focus in the field.
        <div ref={popupRef} className="transfer-search__popup" onMouseDown={keepFocus}>
          {options.length > 0 ? (
            <ul id={listId} role="listbox" aria-label={listLabel} className="transfer-search__list">
              {options.map((option, index) => {
                const optionDisabled = !enabledAt(index);
                const active = index === activeIndex;
                return (
                  <li
                    key={optionKey(option)}
                    id={optionId(index)}
                    role="option"
                    aria-selected={active}
                    aria-disabled={optionDisabled || undefined}
                    className="transfer-search__option"
                    data-active={active ? 'true' : undefined}
                    onMouseMove={() => {
                      if (!optionDisabled && !active) setActiveIndex(index);
                    }}
                    onClick={() => pick(index)}
                  >
                    {renderOption(option, index)}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {status ? <div className="transfer-search__status">{status}</div> : null}
        </div>
      ) : null}
      <span className="sr-only" aria-live="polite">{visible ? announcement ?? '' : ''}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Transfer item search                                               */
/* ------------------------------------------------------------------ */

export type TransferItemSearchProps = {
  /** Free counts are for this site; the field is disabled until it is set. */
  fromLocationId: string | null;
  /** With `eventId`, the event's shortage is suggested when the event's site is this site. */
  toLocationId?: string | null;
  /** Shown with the first match ("14 free at Cincinnati Base"). */
  fromSiteName?: string | null;
  /** The transfer window (ISO), used for free counts and units on other transfers. */
  window: { shipAt: string | null; returnAt: string | null };
  /** With an event at the To site, matches suggest its shortfall ("Add 8"). */
  eventId?: string | null;
  onPick: (result: TransferItemSearchResult) => void;
  label?: string;
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
};

/** True when a match can't be added: none free, or a unit that can't be allocated (in repair…). */
export function isTransferItemUnavailable(result: TransferItemSearchResult): boolean {
  if (result.asset) return !result.asset.available;
  return result.freeAtFrom != null && result.freeAtFrom <= 0;
}

function trackingLabel(result: TransferItemSearchResult): string | null {
  if (result.tracking === 'serial') return 'each unit';
  if (result.tracking === 'quantity') return 'by quantity';
  return null;
}

function optionDetail(result: TransferItemSearchResult): string {
  if (result.asset) {
    return [result.name, result.sku].filter(Boolean).join(' · ');
  }
  if (result.onOtherTransfers > 0) {
    const units = result.onOtherTransfers === 1 ? '1 on another transfer' : `${result.onOtherTransfers.toLocaleString()} on other transfers`;
    return [result.sku, `${units} this week`].filter(Boolean).join(' · ');
  }
  return [result.sku, trackingLabel(result)].filter(Boolean).join(' · ');
}

function availabilityText(result: TransferItemSearchResult, siteName: string | null): string | null {
  // Units are only returned when they are at the From site.
  if (result.asset) return result.asset.available ? 'At this site' : result.asset.note ?? 'Not available';
  if (result.freeAtFrom == null) return null;
  if (result.freeAtFrom <= 0) return 'None free';
  const count = `${result.freeAtFrom.toLocaleString()} free`;
  return siteName ? `${count} at ${siteName}` : count;
}

function addLabel(result: TransferItemSearchResult): string {
  return !result.asset && result.suggestedQty && result.suggestedQty > 1 ? `Add ${result.suggestedQty.toLocaleString()}` : 'Add';
}

function resultKey(result: TransferItemSearchResult): string {
  return result.key;
}

/**
 * Find catalog items by name, SKU or serial, with what is free at the From site for the window.
 * Picking a match calls `onPick` and clears the field for the next search.
 */
export default function TransferItemSearch({
  fromLocationId,
  toLocationId = null,
  fromSiteName,
  window: searchWindow,
  eventId = null,
  onPick,
  label = 'Find items by name, SKU or serial',
  placeholder,
  inputRef,
  disabled = false,
}: TransferItemSearchProps) {
  const [text, setText] = React.useState('');
  const term = useDebouncedText(text);
  const typing = text.trim() !== term;
  const searchable = Boolean(fromLocationId) && term.length >= MIN_QUERY_LENGTH;

  const params = React.useMemo<TransferItemSearchParams>(() => ({
    fromLocationId: fromLocationId ?? '',
    toLocationId: toLocationId || null,
    query: term,
    shipAt: searchWindow.shipAt,
    returnAt: searchWindow.returnAt,
    eventId: eventId || null,
  }), [eventId, fromLocationId, searchWindow.returnAt, searchWindow.shipAt, term, toLocationId]);

  const searchQuery = useQuery({
    queryKey: transferCreateQueryKeys.itemSearch(params),
    queryFn: () => searchTransferItems(params),
    enabled: searchable && !disabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const results = React.useMemo(
    () => (searchable && searchQuery.data ? searchQuery.data : []),
    [searchQuery.data, searchable],
  );
  const loading = searchable && (typing || searchQuery.isFetching);

  let status: React.ReactNode = null;
  if (text.trim().length > 0 && text.trim().length < MIN_QUERY_LENGTH) {
    status = 'Type at least 2 characters.';
  } else if (searchQuery.isError && searchable) {
    status = searchQuery.error instanceof Error && searchQuery.error.message
      ? searchQuery.error.message
      : 'Search is not available right now. Try again.';
  } else if (searchable && !loading && results.length === 0 && searchQuery.isSuccess) {
    status = `No items match “${term}”.`;
  } else if (loading && results.length === 0) {
    status = 'Searching…';
  }

  const announcement = loading
    ? ''
    : results.length > 0
      ? `${results.length.toLocaleString()} ${results.length === 1 ? 'match' : 'matches'}. Use the arrow keys, then Enter to add.`
      : typeof status === 'string' ? status : '';

  const handleSelect = React.useCallback((result: TransferItemSearchResult) => {
    onPick(result);
    setText('');
  }, [onPick]);

  const renderOption = React.useCallback((result: TransferItemSearchResult, index: number) => {
    const unavailable = isTransferItemUnavailable(result);
    const availability = availabilityText(result, index === 0 ? fromSiteName ?? null : null);
    return (
      <>
        <span className="transfer-search__option-main">
          <strong>{result.asset ? result.asset.serial ?? result.asset.barcode ?? result.name : result.name}</strong>
          <small>{optionDetail(result)}</small>
        </span>
        {availability ? (
          <span className="transfer-search__avail" data-tone={unavailable ? 'none' : 'free'}>{availability}</span>
        ) : <span aria-hidden="true" />}
        <span className="transfer-search__add" data-disabled={unavailable ? 'true' : undefined}>{addLabel(result)}</span>
      </>
    );
  }, [fromSiteName]);

  return (
    <SearchCombobox
      label={label}
      placeholder={fromLocationId ? placeholder ?? 'Name, SKU or serial' : 'Choose the From site to search items'}
      value={text}
      onValueChange={setText}
      options={results}
      optionKey={resultKey}
      renderOption={renderOption}
      isOptionDisabled={isTransferItemUnavailable}
      onSelect={handleSelect}
      listLabel="Matching items"
      listEnabled={text.trim().length > 0}
      status={status}
      announcement={announcement}
      busy={loading}
      disabled={disabled || !fromLocationId}
      inputRef={inputRef}
    />
  );
}
